<template>
  <div>
    <s-page class="max-w-2xl mx-auto">
      <s-header>
        {{ stepTitle }}
        <template #sub-heading>
          <template
            v-if="
              step === 'po-match' &&
              !alreadyReceivedInvoice &&
              (matchedInvoice || pendingInvoicesForVendor.length > 0)
            "
          >
            <button
              @click="headerBack"
              class="mr-2 text-gray-400 hover:text-gray-700 transition-colors font-normal"
            >
              ←</button
            >Select a purchase order
          </template>
        </template>
      </s-header>

      <!-- ── Step: Vendor Select ──────────────────────── -->
      <section v-if="step === 'vendor-select'" class="space-y-3">
        <!-- Scan to Select — unified container -->

        <!-- State 1: initial prompt -->
        <button
          v-if="!scanSelectOpen"
          @click="
            scanSelectOpen = true
            openScanCamera()
          "
          class="w-full p-4 sm:p-6 rounded-lg cursor-pointer bg-red-600 hover:bg-red-700 text-white transition-colors"
        >
          <div class="flex items-center">
            <div class="w-10 h-10 flex-shrink-0">
              <unicon name="camera" fill="white" />
            </div>
            <div class="ml-3 flex-1">
              <div class="text-2xl sm:text-3xl leading-none font-bold">
                Scan Invoice
              </div>
              <div class="mt-1 text-sm font-medium opacity-80">
                Camera or Upload - We'll find your invoice
              </div>
            </div>
          </div>
        </button>

        <!-- State 2: strip view (open, 0 or more images) -->
        <div v-else class="rounded-lg bg-gray-200 p-3 sm:p-4">
          <!-- Image strip -->
          <div class="relative flex flex-wrap justify-center gap-2 mb-3 pt-2">
            <div
              v-for="img in scanImages"
              :key="img.id"
              class="relative w-1/4 flex-shrink-0 cursor-pointer"
              @click="scanPreviewImage = img.downloadURL"
            >
              <img
                :src="img.downloadURL"
                class="w-full h-28 object-cover rounded-lg block"
                :class="scanProcessing ? 'opacity-60' : ''"
              />
              <div
                v-if="img.uploading"
                class="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 rounded-lg"
              >
                <div
                  class="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"
                ></div>
              </div>
              <button
                v-if="!scanProcessing"
                @click.stop="removeScanImage(img)"
                style="position: absolute; top: -6px; right: -6px"
                class="z-10 w-5 h-5 flex items-center justify-center rounded-full bg-white text-gray-700 shadow text-xs font-bold leading-none border border-gray-300"
              >
                ✕
              </button>
            </div>
            <!-- Add / initial slot -->
            <button
              v-if="scanImages.length < 3"
              @click="!scanProcessing && openScanCamera()"
              class="w-1/4 flex-shrink-0 h-28 rounded-lg border-2 border-dashed flex items-center justify-center text-3xl transition-colors"
              :class="
                scanProcessing
                  ? 'border-gray-300 text-gray-300 cursor-default'
                  : 'border-gray-400 hover:border-gray-500 text-gray-400 hover:text-gray-600 cursor-pointer'
              "
            >
              +
            </button>
            <!-- Scan line overlay -->
            <div
              v-if="scanProcessing"
              class="scan-line-overlay pointer-events-none"
            ></div>
          </div>

          <!-- Retake reason -->
          <div
            v-if="scanRetakeReason && !scanProcessing"
            class="mb-2 p-3 rounded-lg bg-red-100 border border-red-200 text-sm text-red-700 font-medium"
          >
            <span class="font-bold">Retake needed: </span>{{ scanRetakeReason }}
          </div>
          <!-- Scan error -->
          <div
            v-if="scanError && !scanProcessing"
            class="mb-2 p-3 rounded-lg bg-gray-300 text-sm text-gray-600"
          >
            {{ scanError }}
          </div>

          <!-- Analyze (only once there's at least one image) -->
          <template v-if="scanImages.length > 0">
            <!-- Analyzing — full width, no cancel while in-flight -->
            <div
              v-if="scanProcessing"
              class="w-full px-4 py-3 rounded-lg bg-red-600 text-white flex items-center justify-center gap-3"
            >
              <div class="flex items-center gap-1">
                <div
                  class="w-2 h-2 rounded-full bg-white animate-bounce"
                  style="animation-delay: 0ms"
                ></div>
                <div
                  class="w-2 h-2 rounded-full bg-white animate-bounce"
                  style="animation-delay: 175ms"
                ></div>
                <div
                  class="w-2 h-2 rounded-full bg-white animate-bounce"
                  style="animation-delay: 350ms"
                ></div>
              </div>
              <span class="text-sm font-semibold tracking-wide"
                >Processing scan</span
              >
            </div>
            <!-- Ready / Uploading — Cancel sits to the right -->
            <div v-else class="flex items-center gap-2">
              <!-- Ready to analyze -->
              <button
                v-if="!scanImages.some((i) => i.uploading)"
                @click="processScanSelect"
                class="flex-1 p-3 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors flex items-center justify-center gap-2"
              >
                <div class="w-5 h-5 flex-shrink-0">
                  <unicon name="search" fill="white" />
                </div>
                <span class="text-base font-bold tracking-wide">{{
                  scanRetakeReason ? 'Re-analyze Invoice' : 'Analyze Invoice'
                }}</span>
              </button>
              <!-- Uploading -->
              <div
                v-else
                class="flex-1 p-3 rounded-lg bg-gray-300 flex items-center justify-center gap-2 text-sm text-gray-500"
              >
                <div
                  class="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin flex-shrink-0"
                ></div>
                Uploading…
              </div>
              <button
                @click="cancelScanSelect"
                class="flex-shrink-0 py-3 px-4 rounded-lg bg-gray-300 text-gray-600 text-sm font-medium hover:bg-gray-400 active:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </template>
          <!-- No images yet — text cancel link -->
          <button
            v-else
            @click="cancelScanSelect"
            class="mt-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            Cancel
          </button>
        </div>

        <!-- Vendor list — blocked while scan is processing -->
        <div :class="{ 'pointer-events-none opacity-40': scanProcessing }">
          <div
            v-if="pendingVendors.length > 0"
            class="pb-1 text-base font-medium text-gray-500 uppercase tracking-wide"
          >
            Vendors delivering today
          </div>
          <div class="space-y-3">
            <div
              v-for="vendor in pendingVendors"
              :key="vendor.id"
              @click="selectVendor(vendor)"
              class="p-4 sm:p-6 rounded-lg cursor-pointer transition-colors"
              :class="
                vendor.inProgressInvoiceId
                  ? 'bg-amber-100 border border-amber-300 hover:bg-amber-200'
                  : 'bg-gray-400 hover:bg-gray-500 text-gray-700 hover:text-gray-800'
              "
            >
              <div class="flex items-center">
                <div class="w-10 h-10 flex-shrink-0">
                  <unicon
                    name="truck"
                    :fill="
                      vendor.inProgressInvoiceId ? '#92400e' : 'currentColor'
                    "
                  />
                </div>
                <div class="ml-3 flex-1">
                  <div
                    class="text-2xl sm:text-3xl leading-none font-bold"
                    :class="vendor.inProgressInvoiceId ? 'text-amber-900' : ''"
                  >
                    {{ vendor.name }}
                  </div>
                  <div
                    class="mt-1 text-sm font-medium"
                    :class="vendor.inProgressInvoiceId ? 'text-amber-700' : ''"
                  >
                    {{
                      vendor.inProgressInvoiceId
                        ? 'Receiving in progress — tap to resume'
                        : `${vendor.pendingCount} pending invoice${
                            vendor.pendingCount !== 1 ? 's' : ''
                          }`
                    }}
                  </div>
                </div>
                <unicon
                  name="angle-right"
                  class="w-6 h-6"
                  :fill="
                    vendor.inProgressInvoiceId ? '#92400e' : 'currentColor'
                  "
                />
              </div>
            </div>
          </div>

          <button
            v-if="otherVendors.length > 0"
            @click="showOtherVendors = !showOtherVendors"
            class="w-full pt-4 text-center text-base font-medium text-gray-500 uppercase tracking-wide"
          >
            {{ showOtherVendors ? '▲ Hide' : '▼' }} Vendors without POs
          </button>

          <div v-if="showOtherVendors" class="space-y-3 mt-3">
            <div
              v-for="vendor in otherVendors"
              :key="vendor.id"
              @click="selectVendor(vendor)"
              class="p-4 sm:p-6 rounded-lg cursor-pointer bg-gray-200 hover:bg-gray-300 text-gray-500 hover:text-gray-700 transition-colors opacity-70"
            >
              <div class="flex items-center">
                <div class="w-10 h-10 flex-shrink-0">
                  <unicon name="truck" />
                </div>
                <div class="ml-3 flex-1">
                  <div class="text-xl leading-none font-bold">
                    {{ vendor.name }}
                  </div>
                </div>
                <unicon name="angle-right" class="w-6 h-6" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ── Step: Scan ──────────────────────────────── -->
      <section v-if="step === 'scan'">
        <p class="text-gray-500 text-base mb-5">
          Photograph each page of the invoice. Keep all text sharp and in frame.
        </p>

        <div
          v-if="scanRetakeReason"
          class="mb-4 p-4 rounded-lg bg-red-100 border border-red-200 text-red-700 text-sm font-medium"
        >
          <span class="font-bold">Retake needed: </span>{{ scanRetakeReason }}
        </div>

        <!-- Thumbnail grid + add/file tiles -->
        <div class="mb-5">
          <div class="flex flex-wrap gap-3">
            <div
              v-for="img in scanImages"
              :key="img.id"
              class="relative w-20 h-28 rounded-lg overflow-hidden bg-gray-300 flex-shrink-0"
            >
              <img :src="img.downloadURL" class="w-full h-full object-cover" />
              <div
                v-if="img.uploading"
                class="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-40"
              >
                <div
                  class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                ></div>
              </div>
              <button
                v-else
                @click="removeScanImage(img)"
                style="position: absolute; top: -6px; right: -6px"
                class="z-10 w-5 h-5 flex items-center justify-center rounded-full bg-white text-gray-700 shadow text-xs font-bold leading-none border border-gray-300"
              >
                ✕
              </button>
            </div>

            <button
              v-if="deviceHasMedia"
              @click="openScanCamera"
              class="w-20 h-28 rounded-lg bg-gray-200 hover:bg-gray-300 flex flex-col items-center justify-center text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            >
              <div class="w-6 h-6 mb-1"><unicon name="camera" /></div>
              <span class="text-xs font-medium">{{
                scanImages.length ? 'Add page' : 'Scan page'
              }}</span>
            </button>

            <label
              class="w-20 h-28 rounded-lg bg-gray-200 hover:bg-gray-300 flex flex-col items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer flex-shrink-0"
            >
              <div class="w-6 h-6 mb-1"><unicon name="image" /></div>
              <span class="text-xs font-medium text-center px-1"
                >Upload file</span
              >
              <input
                type="file"
                accept="image/*,application/pdf"
                multiple
                class="hidden"
                @change="handleScanFiles"
              />
            </label>
          </div>
        </div>

        <div class="space-y-2">
          <button
            v-if="scanImages.length > 0"
            @click="proceedFromScan"
            class="py-2 px-6 inline-flex items-center justify-center text-base sm:text-lg font-medium tracking-wide rounded-lg bg-gray-700 text-white hover:bg-gray-800 transition-colors shadow"
          >
            Continue
          </button>
          <div>
            <button
              @click="skipScan"
              class="text-sm text-gray-400 hover:text-gray-600 py-2 transition-colors"
            >
              Skip — enter manually
            </button>
          </div>
        </div>
      </section>

      <!-- ── Step: PO Match ──────────────────────────── -->
      <section v-if="step === 'po-match'">
        <!-- Already received block -->
        <div
          v-if="alreadyReceivedInvoice"
          class="p-4 sm:p-6 rounded-lg bg-gray-600 text-gray-200"
        >
          <div class="text-xl font-bold mb-1">Already received</div>
          <div class="text-sm text-gray-400">
            Invoice #{{ alreadyReceivedInvoice.invoiceNumber }} was received on
            {{ formatDate(alreadyReceivedInvoice.completedAt) }}.
          </div>
          <button
            @click="resetFlow"
            class="mt-4 py-2 px-4 rounded-lg bg-gray-500 text-gray-200 text-sm font-medium hover:bg-gray-400 transition-colors"
          >
            ← Start over
          </button>
        </div>

        <template v-else>
          <!-- Invoice selector — shown when vendor has 2+ pending invoices -->
          <div v-if="pendingInvoicesForVendor.length > 0">
            <!-- Drill-in: item list for a specific order -->
            <div v-if="expandedInvoice" class="space-y-3">
              <div
                class="pb-1 text-base font-medium text-gray-500 uppercase tracking-wide"
              >
                {{
                  formatDate(
                    expandedInvoice.deliveryDate || expandedInvoice.orderDate
                  )
                }}
                <span v-if="expandedInvoice.invoiceNumber"
                  >&nbsp;· #{{ expandedInvoice.invoiceNumber }}</span
                >
              </div>
              <div class="rounded-lg overflow-hidden bg-gray-200">
                <div
                  v-for="item in itemsForInvoice(expandedInvoice)"
                  :key="item.id"
                  class="flex items-start justify-between gap-3 px-4 py-2.5 border-b border-gray-300 last:border-0 text-left"
                >
                  <div class="flex-1 min-w-0">
                    <div
                      class="text-sm font-semibold text-gray-700 leading-snug"
                    >
                      {{ item.name }}
                    </div>
                    <div
                      v-if="item.code"
                      class="text-xs font-mono text-gray-400 mt-0.5"
                    >
                      #{{ item.code }}
                    </div>
                  </div>
                  <span
                    class="text-xs text-gray-500 flex-shrink-0 text-right mt-0.5"
                    >{{ item.amountOrdered }} {{ item.unit }}</span
                  >
                </div>
                <div
                  v-if="itemsForInvoice(expandedInvoice).length === 0"
                  class="px-4 py-6 text-center text-sm text-gray-500"
                >
                  No items on this order
                </div>
              </div>
              <button
                @click="selectPendingInvoice(expandedInvoice)"
                class="w-full py-3 px-6 rounded-lg bg-gray-700 text-white text-base font-medium hover:bg-gray-800 transition-colors shadow"
              >
                Select this order
              </button>
            </div>

            <!-- List of orders to choose from -->
            <div v-else class="space-y-3">
              <div
                class="pb-1 text-base font-medium text-gray-500 uppercase tracking-wide"
              >
                {{ selectedVendorName || 'Select a purchase order' }}
              </div>
              <div class="text-sm text-gray-400 pb-1">
                Which purchase order does this invoice match?
              </div>
              <div
                v-for="inv in pendingInvoicesForVendor"
                :key="inv.id"
                @click="selectPendingInvoice(inv)"
                class="p-4 sm:p-5 rounded-lg cursor-pointer bg-gray-400 hover:bg-gray-500 text-gray-700 hover:text-gray-800 transition-colors"
              >
                <div class="flex items-center">
                  <div
                    class="w-10 h-10 flex-shrink-0 flex items-center justify-center"
                  >
                    <unicon
                      name="camera"
                      :fill="
                        inv.autoReconciliation &&
                        inv.autoReconciliation.status === 'complete'
                          ? '#38a169'
                          : '#a0aec0'
                      "
                    />
                  </div>
                  <div class="ml-3 flex-1">
                    <div class="text-xl font-bold leading-none">
                      {{ formatDate(inv.deliveryDate || inv.orderDate) }}
                    </div>
                    <div class="mt-1 text-sm font-medium">
                      {{ poItemCount(inv) }} item{{
                        poItemCount(inv) !== 1 ? 's' : ''
                      }}
                      <span v-if="inv.invoiceNumber"
                        >&nbsp;·&nbsp; #{{ inv.invoiceNumber }}</span
                      >
                    </div>
                  </div>
                  <unicon name="angle-right" class="w-6 h-6" />
                </div>
              </div>
              <div
                @click="selectPendingInvoice(null)"
                class="p-4 sm:p-5 rounded-lg cursor-pointer bg-gray-200 hover:bg-gray-300 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <div class="flex items-center">
                  <div class="flex-1 text-base font-medium">
                    Create new order
                  </div>
                  <unicon name="angle-right" class="w-6 h-6" />
                </div>
              </div>
            </div>
          </div>

          <!-- Confirmed match / no-match display -->
          <template v-else>
            <!-- Matched PO -->
            <div
              v-if="matchedInvoice"
              class="rounded-lg bg-gray-400 text-gray-700 mb-4 text-left overflow-hidden"
            >
              <div class="px-4 pt-4 pb-3 sm:px-5 sm:pt-5">
                <div
                  class="text-xs uppercase tracking-wide font-bold text-gray-500 mb-1"
                >
                  Matched purchase order
                </div>
                <div
                  class="text-2xl sm:text-3xl font-bold leading-tight text-gray-800"
                >
                  {{ vendorName(matchedInvoice.vendorId) }}
                </div>
                <div class="mt-1 text-sm font-medium text-gray-600">
                  {{
                    formatDate(
                      matchedInvoice.deliveryDate || matchedInvoice.orderDate
                    )
                  }}
                  &nbsp;·&nbsp; {{ poItemCount(matchedInvoice) }} items
                </div>
                <div
                  v-if="matchedInvoice.invoiceNumber"
                  class="mt-0.5 text-xs font-mono text-gray-500"
                >
                  Invoice #{{ matchedInvoice.invoiceNumber }}
                </div>
              </div>
              <div
                v-if="itemsForInvoice(matchedInvoice).length > 0"
                class="border-t border-gray-500 border-opacity-30 max-h-52 overflow-y-auto"
              >
                <div
                  v-for="item in itemsForInvoice(matchedInvoice)"
                  :key="item.id"
                  class="flex items-start justify-between gap-3 px-4 py-2.5 sm:px-5 border-b border-gray-500 border-opacity-20 last:border-0 text-left"
                >
                  <div class="flex-1 min-w-0">
                    <div
                      class="text-sm font-semibold text-gray-800 leading-snug"
                    >
                      {{ item.name }}
                    </div>
                    <div
                      v-if="item.code"
                      class="text-xs font-mono text-gray-500 mt-0.5"
                    >
                      #{{ item.code }}
                    </div>
                  </div>
                  <span
                    class="text-xs text-gray-600 flex-shrink-0 text-right mt-0.5"
                    >{{ item.amountOrdered
                    }}{{ item.unit ? ' ' + item.unit : '' }}</span
                  >
                </div>
              </div>
            </div>
            <div v-else class="space-y-3 mb-4">
              <!-- Vendor identified card -->
              <div class="rounded-lg bg-gray-400 text-gray-700 overflow-hidden">
                <div class="px-4 pt-4 pb-3 sm:px-5 sm:pt-5">
                  <div
                    class="text-xs uppercase tracking-wide font-bold text-gray-500 mb-1"
                  >
                    ✓ Vendor Identified
                  </div>
                  <div
                    class="text-2xl sm:text-3xl font-bold leading-tight text-gray-800"
                  >
                    {{ selectedVendorName }}
                  </div>
                  <div
                    v-if="scanResult && scanResult.lineItems"
                    class="mt-1 text-sm font-medium text-gray-600"
                  >
                    {{ scanResult.lineItems.length }} item{{
                      scanResult.lineItems.length !== 1 ? 's' : ''
                    }}
                    found on invoice
                    <span v-if="scanImages.length > 0">
                      ·
                      <button
                        class="underline hover:text-gray-800 transition-colors"
                        @click="scanPreviewImage = scanImages[0].downloadURL"
                      >
                        view scan
                      </button></span
                    >
                  </div>
                </div>
              </div>

              <!-- Choose a different vendor — only when vendor was manually selected (not AI-identified via scan) -->
              <button
                v-if="!scanResult"
                @click="headerBack"
                class="w-full py-2 text-sm text-gray-400 hover:text-gray-600 font-medium transition-colors"
              >
                ← Choose a different vendor
              </button>

              <!-- No PO explanation -->
              <div class="p-4 rounded-lg bg-gray-100">
                <div
                  class="text-xs uppercase tracking-wide font-bold text-gray-400 mb-1"
                >
                  No Purchase Order on File
                </div>
                <div class="text-sm text-gray-600">
                  There's no open purchase order for this delivery. You can
                  still receive it — a new order record will be created from the
                  invoice.
                </div>
              </div>

              <!-- Primary CTA -->
              <button
                @click="confirmPOMatch"
                :disabled="creatingInvoice"
                class="w-full py-3 px-6 rounded-lg text-base font-semibold transition-colors shadow disabled:cursor-not-allowed bg-gray-700 text-white hover:bg-gray-800"
              >
                {{ creatingInvoice ? 'Setting up…' : 'Proceed anyway' }}
              </button>
            </div>

            <!-- Invoice image attachment — matched PO case only -->
            <div
              v-if="matchedInvoice"
              class="mt-4 rounded-lg overflow-hidden"
              :class="
                scanImages.length === 0
                  ? 'bg-amber-100 border border-amber-300'
                  : 'bg-gray-200'
              "
            >
              <div
                class="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium"
                :class="
                  scanImages.length === 0 ? 'text-amber-700' : 'text-gray-600'
                "
              >
                <div class="w-4 h-4 flex-shrink-0">
                  <unicon
                    name="camera"
                    :fill="scanImages.length === 0 ? '#b45309' : 'currentColor'"
                  />
                </div>
                <span class="flex-1">{{
                  scanImages.length === 0
                    ? 'Scan required — attach invoice images'
                    : 'Invoice images'
                }}</span>
                <span
                  v-if="scanImages.length > 0"
                  class="text-xs bg-gray-600 text-white rounded-full px-2 py-0.5"
                  >{{ scanImages.length }}</span
                >
              </div>
              <div class="px-4 pb-4 border-t border-gray-300">
                <!-- Add-image controls — locked while analyzing or after successful analysis -->
                <div v-if="!scanProcessing && !scanResult" class="mt-3">
                  <!-- No images yet: single prominent red scan button -->
                  <label
                    v-if="scanImages.length === 0"
                    class="w-full py-3 px-4 rounded-lg bg-red-600 hover:bg-red-700 flex items-center justify-center gap-2 text-base font-semibold text-white transition-colors cursor-pointer"
                  >
                    <div class="w-5 h-5 flex-shrink-0">
                      <unicon name="camera" fill="currentColor" />
                    </div>
                    Scan Invoice
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      multiple
                      class="hidden"
                      @change="handleScanFiles"
                    />
                  </label>
                  <!-- Has images: smaller add-page buttons -->
                  <div v-else class="flex gap-3">
                    <button
                      @click="openScanCamera"
                      class="flex-1 py-3 px-4 rounded-lg bg-gray-300 hover:bg-gray-400 flex items-center justify-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      <div class="w-5 h-5 flex-shrink-0">
                        <unicon name="camera" />
                      </div>
                      Add page
                    </button>
                    <label
                      class="flex-1 py-3 px-4 rounded-lg bg-gray-300 hover:bg-gray-400 flex items-center justify-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
                    >
                      <div class="w-5 h-5 flex-shrink-0">
                        <unicon name="image" />
                      </div>
                      Upload file
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        multiple
                        class="hidden"
                        @change="handleScanFiles"
                      />
                    </label>
                  </div>
                </div>
                <!-- Thumbnails -->
                <div
                  v-if="scanImages.length > 0"
                  class="flex flex-wrap justify-center gap-2 mt-3 pt-2"
                >
                  <div
                    v-for="img in scanImages"
                    :key="img.id"
                    class="relative w-20 h-28 flex-shrink-0 cursor-pointer"
                    @click="scanPreviewImage = img.downloadURL"
                  >
                    <img
                      :src="img.downloadURL"
                      class="w-full h-full object-cover rounded-lg block"
                    />
                    <div
                      v-if="img.uploading"
                      class="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-40 rounded-lg"
                    >
                      <div
                        class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                      ></div>
                    </div>
                    <button
                      v-else-if="!scanProcessing && !scanResult"
                      @click="removeScanImage(img)"
                      style="position: absolute; top: -6px; right: -6px"
                      class="z-10 w-5 h-5 flex items-center justify-center rounded-full bg-white text-gray-700 shadow text-xs font-bold leading-none border border-gray-300"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Analyze scan controls — matched PO case only -->
            <div
              v-if="
                matchedInvoice &&
                scanImages.length > 0 &&
                !scanImages.some((i) => i.uploading)
              "
              class="mt-3"
            >
              <div
                v-if="matchedInvoice && scanResult && !scanRetakeReason"
                class="flex items-center gap-2 text-sm font-medium text-gray-600 px-1"
              >
                <span>✓</span>
                <span
                  >Invoice analyzed — {{ scanResult.lineItems.length }} item{{
                    scanResult.lineItems.length !== 1 ? 's' : ''
                  }}
                  found</span
                >
              </div>
              <div
                v-if="scanRetakeReason"
                class="mb-2 text-sm font-medium text-red-600 px-1"
              >
                {{ scanRetakeReason }}
              </div>
              <button
                v-if="!scanResult"
                @click="initializeAndReconcile"
                :disabled="scanProcessing"
                class="mt-1 py-2 px-5 rounded-lg text-sm font-semibold transition-colors disabled:cursor-not-allowed"
                :class="
                  scanProcessing
                    ? 'bg-gray-200 text-gray-400'
                    : 'bg-gray-600 text-white hover:bg-gray-700'
                "
              >
                {{
                  scanProcessing
                    ? 'Analyzing invoice…'
                    : scanRetakeReason
                    ? 'Re-analyze Invoice'
                    : 'Analyze Invoice'
                }}
              </button>
            </div>

            <div v-if="matchedInvoice" class="mt-4">
              <button
                @click="confirmPOMatch"
                :disabled="creatingInvoice || !scanResult || scanRetakeReason"
                class="w-full py-3 px-6 inline-flex items-center gap-2 justify-center text-base sm:text-lg font-medium tracking-wide rounded-lg disabled:cursor-not-allowed transition-colors shadow"
                :class="
                  !scanResult || scanRetakeReason
                    ? 'bg-gray-300 text-gray-400'
                    : 'bg-gray-700 text-white hover:bg-gray-800'
                "
              >
                {{
                  creatingInvoice
                    ? 'Setting up…'
                    : scanImages.length === 0
                    ? 'Scan required'
                    : !scanResult
                    ? 'Analyze invoice first'
                    : 'Proceed with Receiving'
                }}
                <unicon
                  v-if="scanResult && !scanRetakeReason && !creatingInvoice"
                  name="angle-right"
                  fill="currentColor"
                  class="w-5 h-5"
                />
              </button>
            </div> </template
          ><!-- end confirmed match/no-match -->
        </template>
      </section>

      <!-- ── Scan status banner (shown throughout items step) ── -->
      <template v-if="step === 'items' && scanImages.length > 0">
        <div
          v-if="scanProcessing"
          class="mb-4 p-4 rounded-lg bg-gray-200 text-gray-600"
        >
          <div class="text-sm font-bold">Analyzing invoice scan…</div>
          <div class="text-xs text-gray-500 mt-1">
            Items will be added automatically. You can start receiving now.
          </div>
        </div>
        <div
          v-else-if="scanRetakeReason"
          class="mb-4 p-4 rounded-lg bg-red-100 border border-red-200 text-red-700"
        >
          <div class="text-sm font-bold mb-1">Image quality issue</div>
          <div class="text-sm">{{ scanRetakeReason }}</div>
          <button
            @click="goBackToRescan"
            class="mt-2 text-xs font-semibold underline hover:no-underline"
          >
            Go back and retake
          </button>
        </div>
        <div
          v-else-if="scanError"
          class="mb-4 p-4 rounded-lg bg-gray-200 text-gray-600"
        >
          <div class="text-sm font-bold mb-1">Scan processing failed</div>
          <div class="text-xs">{{ scanError }}</div>
        </div>
        <div
          v-else-if="scanResult"
          class="mb-4 p-3 rounded-lg bg-gray-200 text-gray-600"
        >
          <div class="text-sm font-bold">Invoice scanned ✓</div>
          <div class="text-xs mt-0.5">
            {{ allItemsList.length }} item{{
              allItemsList.length !== 1 ? 's' : ''
            }}
            found on this order
          </div>
        </div>
      </template>

      <!-- ── Step: Items ──────────────────────────────── -->
      <receiving-item-card
        v-if="step === 'items' && currentItem && currentItem.type === 'product'"
        :product-id="currentItem.productId"
        :invoice-id="workingInvoiceId"
        :item-index="currentItemIndex"
        :item-total="allItemsList.length"
        :scan-quantity="scanQuantityForItem(currentItem.productId)"
        @accept="acceptItem"
        @reject="openRejectionFor"
        @go-back="goBackItem"
      />

      <!-- Unknown item (no product match — found on invoice scan) -->
      <div
        v-if="activeUnknownItem"
        class="fixed inset-0 flex flex-col bg-gray-900"
        style="z-index: 55"
      >
        <div
          class="pt-6 pb-2 text-center text-xs uppercase tracking-widest font-bold text-gray-500"
        >
          {{
            step === 'items' ? currentItemIndex + 1 : currentUnknownIndex + 1
          }}
          of {{ step === 'items' ? allItemsList.length : unknownItems.length }}
        </div>
        <div
          class="flex-1 flex flex-col items-center justify-center px-8 text-center"
        >
          <div class="mb-6 w-full max-w-sm px-4 py-3 rounded-lg bg-red-700">
            <div
              class="text-xs uppercase tracking-widest font-bold text-red-200 mb-1"
            >
              Unknown item
            </div>
            <div class="text-sm text-white">
              Not in the system — found on invoice scan
            </div>
          </div>
          <div
            class="text-3xl sm:text-4xl font-bold text-white leading-tight mb-8"
          >
            {{ activeUnknownItem.description }}
          </div>
          <div class="text-center mb-6">
            <div
              class="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2"
            >
              Invoiced Qty
            </div>
            <div
              class="text-7xl sm:text-8xl font-bold text-teal-400 leading-none"
            >
              {{
                activeUnknownItem.quantity != null
                  ? activeUnknownItem.quantity
                  : 0
              }}
            </div>
          </div>
        </div>
        <div class="flex border-t border-gray-700 flex-shrink-0">
          <button
            @click="goBackActiveUnknown"
            class="py-5 px-6 text-gray-500 hover:text-gray-300 hover:bg-gray-800 transition-colors border-r border-gray-700"
          >
            <unicon name="arrow-left" class="w-5 h-5" fill="currentColor" />
          </button>
          <button
            @click="rejectUnknownItem(activeUnknownItem)"
            class="flex-1 py-5 text-base font-semibold text-red-400 hover:bg-gray-800 transition-colors border-r border-gray-700"
          >
            Return
          </button>
          <button
            @click="startAcceptUnknown(activeUnknownItem)"
            class="flex-1 py-5 text-base font-semibold text-white hover:bg-gray-800 transition-colors"
          >
            Accept
          </button>
        </div>
      </div>

      <!-- Empty state: no items on order yet -->
      <section v-if="step === 'items' && !currentItem" class="space-y-4">
        <div class="p-4 rounded-lg bg-gray-200 text-gray-600 text-sm">
          No items found on this order. Add products to receive below.
        </div>

        <div class="rounded-lg overflow-hidden bg-gray-600">
          <div class="px-4 py-3 border-b border-gray-500">
            <input
              v-model="itemSearch"
              type="text"
              placeholder="Search products…"
              class="w-full appearance-none block bg-gray-500 text-gray-200 border border-gray-500 rounded-lg py-2 px-3 text-sm focus:outline-none focus:bg-gray-400"
            />
          </div>
          <div class="max-h-60 overflow-y-auto">
            <div
              v-for="prod in filteredAddableProducts"
              :key="prod.id"
              @click="addProductToReceiving(prod.id)"
              class="flex items-center justify-between px-4 py-3 border-b border-gray-500 last:border-0 cursor-pointer hover:bg-gray-500 text-gray-200 transition-colors"
            >
              <span class="text-sm font-medium">{{ prod.name }}</span>
              <unicon
                name="plus-circle"
                class="w-4 h-4 text-gray-400"
                fill="currentColor"
              />
            </div>
            <div
              v-if="filteredAddableProducts.length === 0"
              class="px-4 py-6 text-center text-sm text-gray-400"
            >
              No products found
            </div>
          </div>
        </div>

        <button
          @click="step = 'mispick'"
          class="py-2 px-6 inline-flex items-center justify-center text-base sm:text-lg font-medium tracking-wide rounded-lg bg-gray-700 text-white hover:bg-gray-800 transition-colors shadow"
        >
          Continue without items
        </button>
      </section>

      <!-- ── Step: Extras (scan items not on PO) ──────── -->
      <receiving-item-card
        v-if="step === 'extras' && currentExtraItem"
        :product-id="currentExtraItem.matchedProductId"
        :invoice-id="workingInvoiceId"
        :item-index="currentExtraIndex"
        :item-total="extraItems.length"
        :scan-quantity="currentExtraItem.quantity || null"
        :is-extra="true"
        @accept="acceptExtraItem"
        @reject="rejectExtraItem"
        @go-back="goBackExtra"
      />

      <!-- ── Step: Mispick ────────────────────────────── -->
      <section v-if="step === 'mispick'" class="space-y-4">
        <!-- Input form -->
        <receiving-mispick-form
          ref="mispickForm"
          :is-developer-or-admin="isDeveloperOrAdmin"
          :products="addableProducts"
          @add="addMispickItem"
        />

        <div class="pt-4 flex items-center gap-3">
          <button
            @click="backFromMispick"
            class="py-2 px-4 rounded-lg bg-gray-200 text-gray-600 text-base font-medium hover:bg-gray-300 transition-colors"
          >
            <div class="w-4 h-4 inline-block align-middle">
              <unicon name="arrow-left" />
            </div>
          </button>
          <button
            @click="step = 'confirmation'"
            class="flex-1 py-2 px-6 inline-flex items-center justify-center text-base sm:text-lg font-medium tracking-wide rounded-lg bg-gray-700 text-white hover:bg-gray-800 transition-colors shadow"
          >
            {{
              mispickItems.length > 0
                ? 'Done — Review order'
                : 'No extras — Review order'
            }}
          </button>
        </div>
      </section>

      <!-- ── Step: Confirmation ──────────────────────── -->
      <receiving-confirmation
        v-if="step === 'confirmation'"
        :invoice-id="workingInvoiceId"
        :mispick-items="mispickItems"
        :scan-result="scanResult"
        @submit="submitReceiving"
        @back="step = 'mispick'"
        @product-added="handleConfirmationProductAdd"
        @edit-item="
          (productId) => {
            editingItem = productId
          }
        "
        @edit-mispick="
          (idx) => {
            editingMispickIndex = idx
          }
        "
      />

      <!-- ── Exit Receiving footer ─────────────────────── -->
      <div
        v-if="step !== 'complete' && step !== 'vendor-select'"
        class="mt-10 pt-6 border-t border-gray-200 flex justify-center"
      >
        <button
          @click="confirmExit"
          class="py-2 px-6 rounded-lg bg-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-300 active:bg-gray-300 transition-colors"
        >
          Exit Receiving
        </button>
      </div>

      <!-- ── Mispick edit form overlay (unmatched items) ── -->
      <div
        v-if="
          editingMispickIndex !== null &&
          mispickItems[editingMispickIndex] &&
          !mispickItems[editingMispickIndex].productId
        "
        class="fixed inset-0 flex flex-col justify-end"
        style="z-index: 55; background: rgba(0,0,0,0.6)"
        @click.self="editingMispickIndex = null"
      >
        <div class="bg-gray-100 rounded-t-2xl flex flex-col" style="max-height: 85vh">
          <!-- Handle + header -->
          <div class="flex-shrink-0">
            <div class="flex justify-center pt-3 pb-1">
              <div class="w-10 h-1 bg-gray-300 rounded-full"></div>
            </div>
            <div class="px-4 pt-2 pb-3 flex items-center gap-3 border-b border-gray-300">
              <button
                @click="editingMispickIndex = null"
                class="text-gray-400 hover:text-gray-700 p-1"
              >
                <unicon name="arrow-left" class="w-5 h-5" fill="currentColor" />
              </button>
              <span class="text-base font-semibold text-gray-700 flex-1">Edit item</span>
              <button
                @click="removeMispickFromEdit"
                class="text-sm font-medium text-red-600 hover:text-red-800"
              >
                Remove
              </button>
            </div>
          </div>
          <div class="overflow-y-auto px-4 pt-4 pb-8">
            <receiving-mispick-form
              :is-developer-or-admin="isDeveloperOrAdmin"
              :products="addableProducts"
              :edit-item="mispickItems[editingMispickIndex]"
              @add="updateMispickItem"
              @cancel="editingMispickIndex = null"
            />
          </div>
        </div>
      </div>

      <!-- ── Edit overlay (from confirmation tap) ─────── -->
      <receiving-item-card
        v-if="editingItem"
        :product-id="editingItem"
        :invoice-id="workingInvoiceId"
        :item-index="0"
        :item-total="1"
        :scan-quantity="scanQuantityForItem(editingItem)"
        :is-edit="true"
        @accept="acceptEditItem"
        @reject="rejectEditItem"
        @go-back="editingItem = null"
      />

      <!-- ── Mispick edit overlay (matched / has productId) ── -->
      <div
        v-if="
          editingMispickIndex !== null &&
          mispickItems[editingMispickIndex] &&
          mispickItems[editingMispickIndex].productId
        "
        class="fixed inset-0 flex flex-col justify-end"
        style="z-index: 55; background: rgba(0, 0, 0, 0.6)"
        @click.self="editingMispickIndex = null"
      >
        <div
          class="bg-gray-100 rounded-t-2xl overflow-hidden shadow-xl"
        >
          <div class="flex justify-center pt-3 pb-1">
            <div class="w-10 h-1 bg-gray-300 rounded-full"></div>
          </div>
          <div class="px-6 pt-6 pb-4">
            <div
              class="text-xs uppercase tracking-wide text-gray-500 font-bold mb-1"
            >
              Not on order
            </div>
            <div class="text-2xl font-bold text-gray-800 leading-tight">
              {{ mispickItems[editingMispickIndex].description }}
            </div>
          </div>

          <div class="px-6 pb-5">
            <!-- Status -->
            <div
              v-if="
                mispickItems[editingMispickIndex].disposition === 'returned'
              "
              class="mb-4 px-4 py-3 rounded-lg bg-red-600 text-white text-sm font-semibold flex items-center gap-2"
            >
              <span>↩</span>
              <span
                >Returning to vendor &nbsp;·&nbsp; Qty
                {{ mispickItems[editingMispickIndex].quantity }}</span
              >
            </div>
            <div
              v-else
              class="mb-4 px-4 py-3 rounded-lg bg-gray-200 text-sm text-gray-600"
            >
              <span class="font-medium">Qty:</span>
              {{ mispickItems[editingMispickIndex].quantity }}
              &nbsp;·&nbsp;
              <span class="text-teal-700 font-medium">Accepting</span>
            </div>

            <button
              @click="toggleMispickDisposition"
              class="w-full py-3 px-6 rounded-lg text-base font-medium transition-colors"
              :class="
                mispickItems[editingMispickIndex].disposition === 'returned'
                  ? 'bg-gray-700 text-white hover:bg-gray-800'
                  : 'bg-red-700 text-white hover:bg-red-800'
              "
            >
              {{
                mispickItems[editingMispickIndex].disposition === 'returned'
                  ? 'Mark Accepted'
                  : 'Return to Vendor'
              }}
            </button>
          </div>

          <div class="flex border-t border-gray-200">
            <button
              @click="removeMispickFromEdit"
              class="flex-1 py-4 text-red-600 text-sm font-semibold hover:bg-red-50 transition-colors border-r border-gray-200"
            >
              Remove item
            </button>
            <button
              @click="editingMispickIndex = null"
              class="flex-1 py-4 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>

      <!-- ── Step: Complete ──────────────────────────── -->
      <section v-if="step === 'complete'" class="pt-8 text-center">
        <div
          class="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gray-200 mb-6"
        >
          <unicon name="check-circle" class="w-14 h-14 text-teal-600" />
        </div>

        <div class="text-gray-500 text-base mb-8">
          {{ selectedVendorName }} invoice recorded.
        </div>
        <button
          @click="resetFlow"
          class="py-2 px-6 inline-flex items-center justify-center text-base sm:text-lg font-medium tracking-wide rounded-lg bg-gray-700 text-white hover:bg-gray-800 transition-colors shadow"
        >
          Back to Receiving
        </button>
      </section>
    </s-page>

    <!-- file picker for scan images -->
    <input
      ref="fallbackFileInput"
      type="file"
      accept="image/*,application/pdf"
      multiple
      class="hidden"
      @change="handleScanFiles"
    />

    <!-- ── Return-to-vendor confirmation overlay ─────── -->
    <div
      v-if="returnConfirm"
      class="fixed inset-0 flex items-center justify-center p-6 bg-gray-900 bg-opacity-70"
      style="z-index: 65"
    >
      <div
        class="w-full max-w-sm rounded-2xl bg-gray-700 overflow-hidden shadow-xl text-center"
      >
        <div class="px-8 pt-8 pb-6">
          <div
            class="text-xs uppercase tracking-widest font-bold text-red-400 mb-4"
          >
            Return to Vendor
          </div>
          <div class="text-3xl sm:text-4xl font-bold text-white leading-tight">
            {{ returnConfirm.productName }}
          </div>
          <div v-if="returnConfirm.qty" class="text-gray-400 text-sm mt-2">
            Qty: {{ returnConfirm.qty }}
          </div>
          <div class="text-gray-400 text-sm mt-2">
            Will be marked for credit on this invoice.
          </div>
        </div>
        <div class="flex border-t border-gray-600">
          <button
            @click="returnConfirm = null"
            class="flex-1 py-4 text-gray-300 text-base font-semibold hover:bg-gray-600 transition-colors border-r border-gray-600"
          >
            Cancel
          </button>
          <button
            @click="confirmReturn"
            class="flex-1 py-4 text-red-400 text-base font-semibold hover:bg-gray-600 transition-colors"
          >
            Confirm Return
          </button>
        </div>
      </div>
    </div>

    <!-- ── Accept unknown item overlay ───────────────── -->
    <div
      v-if="unknownAcceptModal"
      class="fixed inset-0 flex flex-col justify-end"
      style="z-index: 66; background: rgba(0, 0, 0, 0.5)"
      @click.self="unknownAcceptModal = null"
    >
      <div
        class="bg-gray-100 rounded-t-lg overflow-hidden max-h-screen overflow-y-auto pb-10"
      >
        <!-- Handle -->
        <div class="flex justify-center pt-3 pb-4">
          <div class="w-10 h-1 bg-gray-400 rounded-full"></div>
        </div>

        <div class="px-4 sm:px-6">
          <div
            class="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-1"
          >
            Unknown item
          </div>
          <div
            class="text-2xl sm:text-3xl font-bold text-gray-800 leading-tight mb-5"
          >
            {{ unknownAcceptModal.item.description }}
          </div>

          <!-- Product match -->
          <div class="mb-5">
            <label
              class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            >
              Match to existing product
              <span class="normal-case font-normal text-gray-500"
                >(optional)</span
              >
            </label>
            <input
              v-model="unknownProductSearch"
              type="text"
              placeholder="Search products…"
              class="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
            />
            <div
              v-if="unknownProductSearch && filteredUnknownProducts.length > 0"
              class="mt-1 rounded-lg bg-white overflow-hidden max-h-44 overflow-y-auto border border-gray-200"
            >
              <div
                v-for="prod in filteredUnknownProducts"
                :key="prod.id"
                @click="selectUnknownProductMatch(prod)"
                class="px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-0"
                :class="
                  unknownAcceptModal.matchedProductId === prod.id
                    ? 'bg-gray-100 font-semibold'
                    : ''
                "
              >
                {{ prod.name }}
              </div>
            </div>
            <div
              v-if="unknownAcceptModal.matchedProductId"
              class="mt-2 flex items-center gap-2 text-sm text-teal-700"
            >
              <span
                >✓
                {{
                  (productById(unknownAcceptModal.matchedProductId) || {}).name
                }}</span
              >
              <button
                @click="
                  unknownAcceptModal.matchedProductId = null
                  unknownProductSearch = ''
                "
                class="text-gray-500 text-xs underline"
              >
                clear
              </button>
            </div>
          </div>

          <!-- Photo of Label -->
          <div class="mb-5">
            <label
              class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            >
              Photo of Label
              <span
                v-if="!unknownAcceptModal.matchedProductId"
                class="normal-case font-normal text-red-500"
                >(required)</span
              >
              <span v-else class="normal-case font-normal text-gray-500"
                >(optional)</span
              >
            </label>
            <div
              v-if="unknownAcceptModal.labelPhotoUrl"
              class="flex items-center gap-3"
            >
              <img
                :src="unknownAcceptModal.labelPhotoUrl"
                class="w-20 h-20 object-cover rounded-lg"
              />
              <div>
                <div
                  v-if="unknownAcceptModal.labelPhotoUploading"
                  class="text-xs text-gray-500"
                >
                  Uploading…
                </div>
                <button
                  v-else
                  @click="unknownAcceptModal.labelPhotoUrl = null"
                  class="text-sm text-red-600 hover:text-red-800"
                >
                  Retake
                </button>
              </div>
            </div>
            <label
              v-else
              class="w-full py-3 px-4 rounded-lg bg-gray-700 text-white text-base font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 cursor-pointer"
            >
              <div class="w-4 h-4">
                <unicon name="camera" fill="currentColor" />
              </div>
              Add Photo
              <input
                type="file"
                accept="image/*"
                class="hidden"
                @change="handleUnknownLabelPhoto"
              />
            </label>
          </div>

          <!-- Photo of Package -->
          <div class="mb-5">
            <label
              class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            >
              Photo of Package
              <span
                v-if="!unknownAcceptModal.matchedProductId"
                class="normal-case font-normal text-red-500"
                >(required)</span
              >
              <span v-else class="normal-case font-normal text-gray-500"
                >(optional)</span
              >
            </label>
            <div
              v-if="unknownAcceptModal.packagePhotoUrl"
              class="flex items-center gap-3"
            >
              <img
                :src="unknownAcceptModal.packagePhotoUrl"
                class="w-20 h-20 object-cover rounded-lg"
              />
              <div>
                <div
                  v-if="unknownAcceptModal.packagePhotoUploading"
                  class="text-xs text-gray-500"
                >
                  Uploading…
                </div>
                <button
                  v-else
                  @click="unknownAcceptModal.packagePhotoUrl = null"
                  class="text-sm text-red-600 hover:text-red-800"
                >
                  Retake
                </button>
              </div>
            </div>
            <label
              v-else
              class="w-full py-3 px-4 rounded-lg bg-gray-700 text-white text-base font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 cursor-pointer"
            >
              <div class="w-4 h-4">
                <unicon name="camera" fill="currentColor" />
              </div>
              Add Photo
              <input
                type="file"
                accept="image/*"
                class="hidden"
                @change="handleUnknownPackagePhoto"
              />
            </label>
          </div>

          <div class="flex gap-3 pt-5 pb-2">
            <button
              @click="
                unknownAcceptModal = null
                unknownProductSearch = ''
              "
              class="py-3 px-4 rounded-lg bg-gray-300 text-gray-600 text-base font-medium hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
            <button
              @click="confirmAcceptUnknown"
              :disabled="
                !unknownAcceptModal.matchedProductId &&
                (!unknownAcceptModal.labelPhotoUrl ||
                  unknownAcceptModal.labelPhotoUploading ||
                  !unknownAcceptModal.packagePhotoUrl ||
                  unknownAcceptModal.packagePhotoUploading)
              "
              class="flex-1 py-3 px-6 inline-flex items-center justify-center text-base font-medium tracking-wide rounded-lg bg-gray-700 text-white hover:bg-gray-800 disabled:opacity-40 transition-colors"
            >
              Confirm Accept
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Mispick put-away modal ────────────────────── -->
    <div
      v-if="mispickPutAwayLocation"
      class="fixed inset-0 flex items-center justify-center p-6 bg-gray-900 bg-opacity-70"
      style="z-index: 62"
    >
      <div
        class="w-full max-w-sm rounded-2xl bg-gray-700 overflow-hidden shadow-xl text-center"
      >
        <div class="px-8 pt-8 pb-6">
          <div
            class="text-xs uppercase tracking-widest font-bold text-gray-400 mb-3"
          >
            Put Away
          </div>
          <div class="text-4xl font-bold text-white leading-tight capitalize">
            {{ mispickPutAwayLocation }}
          </div>
        </div>
        <div class="border-t border-gray-600">
          <button
            @click="mispickPutAwayLocation = null"
            class="w-full py-5 text-white text-lg font-semibold hover:bg-gray-600 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>

    <!-- ── Scan image preview lightbox ─────────────── -->
    <div
      v-if="scanPreviewImage"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-90"
      style="z-index: 80"
      @click="scanPreviewImage = null"
    >
      <img
        :src="scanPreviewImage"
        class="max-w-full max-h-full object-contain p-4"
      />
    </div>

    <!-- ── Rejection overlay ─────────────────────────── -->
    <receiving-rejection-modal
      v-if="rejectionTarget"
      :product-id="rejectionTarget"
      :invoice-id="workingInvoiceId"
      :scan-quantity="scanQuantityForItem(rejectionTarget)"
      :is-developer-or-admin="isDeveloperOrAdmin"
      @save="saveRejection"
      @cancel="rejectionTarget = null"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'
import { storage } from '@/firebase.js'
import { firebase } from '@/firebase.js'
import { newId } from '@/utils'
import SPage from '@/components/SPage'
import SHeader from '@/components/SHeader'
import ReceivingItemCard from '@/components/ReceivingItemCard'
import ReceivingRejectionModal from '@/components/ReceivingRejectionModal'
import ReceivingMispickForm from '@/components/ReceivingMispickForm'
import ReceivingConfirmation from '@/components/ReceivingConfirmation'

export default {
  name: 'InvoiceReceiving',
  components: {
    SPage,
    SHeader,
    ReceivingItemCard,
    ReceivingRejectionModal,
    ReceivingMispickForm,
    ReceivingConfirmation
  },
  data() {
    return {
      step: 'vendor-select',
      selectedVendorId: null,
      workingInvoiceId: null,
      scanImages: [],
      scanResult: null,
      scanRetakeReason: null,
      matchedInvoice: null,
      pendingInvoicesForVendor: [],
      expandedInvoice: null,
      alreadyReceivedInvoice: null,
      creatingInvoice: false,
      currentItemIndex: 0,
      rejectionTarget: null,
      mispickItems: [],
      showOtherVendors: false,
      scanSelectOpen: false,
      isNewInvoice: false,
      itemSearch: '',
      pendingProductId: null,

      scanProcessing: false,
      scanError: null,
      analyzeMessageIndex: 0,
      analyzeTimer: null,
      analyzeMessages: [
        'Validating image quality…',
        'Reading line items…',
        'Matching products…',
        'Identifying purchase order…',
        'Almost there…'
      ],
      currentExtraIndex: 0,
      returnConfirm: null,
      editingItem: null,
      editingMispickIndex: null,
      currentUnknownIndex: 0,
      unknownAcceptModal: null,
      unknownProductSearch: '',
      scanPreviewImage: null,
      scanResultFromNewFlow: false,
      mispickPutAwayLocation: null
    }
  },
  computed: {
    ...mapGetters('user', ['isAdmin', 'isDeveloper']),
    ...mapGetters('storeModule/vendors', ['vendorsActive', 'vendorName']),
    ...mapGetters('storeModule/invoices', [
      'invoicesToday',
      'invoicesPastIncomplete',
      'invoices',
      'invoiceById'
    ]),
    ...mapGetters('storeModule/products', [
      'isProduct',
      'productById',
      'products'
    ]),
    ...mapGetters('storeModule', [
      'productsNotInInvoiceOrZeroedOutByInvoiceId'
    ]),

    isDeveloperOrAdmin() {
      return this.isAdmin || this.isDeveloper
    },

    activeUnknownItem() {
      if (
        this.step === 'items' &&
        this.currentItem &&
        this.currentItem.type === 'unknown'
      ) {
        return this.currentItem
      }
      if (this.step === 'unknowns' && this.currentUnknownItem) {
        return this.currentUnknownItem
      }
      return null
    },

    deviceHasMedia() {
      return (
        'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices
      )
    },

    selectedVendorName() {
      if (!this.selectedVendorId) return null
      return this.vendorName(this.selectedVendorId)
    },

    pendingInvoiceIds() {
      return [
        ...(this.invoicesToday || []),
        ...(this.invoicesPastIncomplete || [])
      ]
    },

    pendingVendors() {
      const counts = {}
      const inProgressMap = {}
      this.pendingInvoiceIds.forEach((id) => {
        const inv = this.invoiceById(id)
        if (!inv || inv.completed) return
        counts[inv.vendorId] = (counts[inv.vendorId] || 0) + 1
        if (inv.receivingInProgress) inProgressMap[inv.vendorId] = id
      })
      return Object.keys(counts)
        .map((vid) => ({
          id: vid,
          name: this.vendorName(vid),
          pendingCount: counts[vid],
          inProgressInvoiceId: inProgressMap[vid] || null
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
    },

    otherVendors() {
      const pendingIds = new Set(this.pendingVendors.map((v) => v.id))
      return (this.vendorsActive || [])
        .filter((vid) => !pendingIds.has(vid))
        .map((vid) => ({ id: vid, name: this.vendorName(vid) }))
        .sort((a, b) => a.name.localeCompare(b.name))
    },

    receivingItems() {
      if (!this.workingInvoiceId) return []
      const inv = this.invoiceById(this.workingInvoiceId)
      if (!inv) return []
      const posMap = this.scanResultPositionMap
      const zeroIds = this.zeroShippedProductIds
      return Object.keys(inv)
        .filter((key) => {
          if (!this.isProduct(key) || !inv[key]) return false
          if (!(inv[key].amountOrdered > 0 || inv[key].amountOrdered === false))
            return false
          if (zeroIds.has(key)) return false
          return true
        })
        .sort((a, b) => {
          const posA = posMap[a] != null ? posMap[a] : Infinity
          const posB = posMap[b] != null ? posMap[b] : Infinity
          if (posA !== posB) return posA - posB
          const na = (this.productById(a) || {}).name || ''
          const nb = (this.productById(b) || {}).name || ''
          return na.localeCompare(nb)
        })
    },

    zeroShippedProductIds() {
      const ids = new Set()
      if (!this.scanResult || !this.scanResult.lineItems) return ids
      const zeroLines = this.scanResult.lineItems.filter(
        (l) =>
          l.quantity !== null &&
          l.quantity !== undefined &&
          Number(l.quantity) === 0
      )
      if (zeroLines.length === 0) return ids

      // Direct matches via matchedProductId
      zeroLines.forEach((l) => {
        if (l.matchedProductId) ids.add(l.matchedProductId)
      })

      // Fuzzy-match unmatched zero-qty lines against PO products by name
      const unmatchedZero = zeroLines.filter(
        (l) => !l.matchedProductId && l.rawText
      )
      if (unmatchedZero.length > 0) {
        const inv = this.workingInvoiceId
          ? this.invoiceById(this.workingInvoiceId)
          : null
        if (inv) {
          const sigWords = (str) =>
            str
              .toLowerCase()
              .replace(/[^a-z0-9\s]/g, '')
              .split(/\s+/)
              .filter((w) => w.length >= 3)
          const poIds = Object.keys(inv).filter(
            (k) => this.isProduct(k) && inv[k]
          )
          unmatchedZero.forEach((scanLine) => {
            const scanWords = sigWords(scanLine.rawText)
            if (!scanWords.length) return
            poIds.forEach((productId) => {
              if (ids.has(productId)) return
              const nameWords = sigWords(
                (this.productById(productId) || {}).name || ''
              )
              const hits = nameWords.filter((w) => scanWords.includes(w)).length
              if (hits >= 2 && hits / nameWords.length >= 0.6)
                ids.add(productId)
            })
          })
        }
      }
      return ids
    },

    scanResultPositionMap() {
      const map = {}
      if (!this.scanResult || !this.scanResult.lineItems) return map

      // Pass 1: items the AI confirmed matched to a product
      this.scanResult.lineItems.forEach((item) => {
        if (item.matchedProductId && item.lineIndex != null) {
          map[item.matchedProductId] = item.lineIndex
        }
      })

      // Pass 2: for scan lines the AI couldn't match, try word-overlap against
      // remaining PO products so they appear at their correct invoice position
      // rather than being grouped at the end. Requires 2+ significant words to match.
      const inv = this.workingInvoiceId
        ? this.invoiceById(this.workingInvoiceId)
        : null
      if (!inv) return map

      const unmatchedPoIds = Object.keys(inv).filter(
        (k) =>
          this.isProduct(k) &&
          inv[k] &&
          (inv[k].amountOrdered > 0 || inv[k].amountOrdered === false) &&
          map[k] == null
      )
      if (unmatchedPoIds.length === 0) return map

      const sigWords = (str) =>
        str
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '')
          .split(/\s+/)
          .filter((w) => w.length >= 3)

      this.scanResult.lineItems
        .filter(
          (item) =>
            !item.matchedProductId && item.lineIndex != null && item.rawText
        )
        .forEach((scanItem) => {
          const scanWords = sigWords(scanItem.rawText)
          if (scanWords.length === 0) return

          let bestId = null
          let bestScore = 0
          let bestHits = 0

          unmatchedPoIds.forEach((productId) => {
            if (map[productId] != null) return
            const nameWords = sigWords(
              (this.productById(productId) || {}).name || ''
            )
            if (nameWords.length === 0) return
            const hits = nameWords.filter((w) => scanWords.includes(w)).length
            const score = hits / nameWords.length
            if (
              hits >= 2 &&
              score >= 0.6 &&
              (score > bestScore || (score === bestScore && hits > bestHits))
            ) {
              bestScore = score
              bestHits = hits
              bestId = productId
            }
          })

          if (bestId) {
            map[bestId] = scanItem.lineIndex
            unmatchedPoIds.splice(unmatchedPoIds.indexOf(bestId), 1)
          }
        })

      return map
    },

    addableProducts() {
      if (!this.workingInvoiceId) return []
      try {
        return this.productsNotInInvoiceOrZeroedOutByInvoiceId(
          this.workingInvoiceId
        )
          .map((id) => ({ id, name: (this.productById(id) || {}).name || id }))
          .sort((a, b) => a.name.localeCompare(b.name))
      } catch {
        return []
      }
    },

    filteredAddableProducts() {
      if (!this.itemSearch) return this.addableProducts
      const q = this.itemSearch.toLowerCase()
      return this.addableProducts.filter((p) =>
        p.name.toLowerCase().includes(q)
      )
    },

    allItemsList() {
      const posMap = this.scanResultPositionMap
      const productEntries = this.receivingItems.map((productId) => ({
        type: 'product',
        productId,
        lineIndex: posMap[productId] != null ? posMap[productId] : Infinity
      }))
      const unknownEntries = this.unknownItems.map((item) => ({
        ...item,
        type: 'unknown',
        lineIndex: !isNaN(parseInt(item.id)) ? parseInt(item.id) : Infinity
      }))
      return [...productEntries, ...unknownEntries].sort((a, b) => {
        if (a.lineIndex !== b.lineIndex) return a.lineIndex - b.lineIndex
        if (a.type !== b.type) return a.type === 'product' ? -1 : 1
        const na =
          a.type === 'product'
            ? (this.productById(a.productId) || {}).name || ''
            : a.description || ''
        const nb =
          b.type === 'product'
            ? (this.productById(b.productId) || {}).name || ''
            : b.description || ''
        return na.localeCompare(nb)
      })
    },

    currentItem() {
      return this.allItemsList[this.currentItemIndex] || null
    },

    extraItems() {
      if (!this.scanResult || !this.scanResult.lineItems) return []
      const poProductIds = new Set(this.receivingItems)
      return this.scanResult.lineItems.filter(
        (item) =>
          item.matchedProductId &&
          !poProductIds.has(item.matchedProductId) &&
          !(
            item.quantity !== null &&
            item.quantity !== undefined &&
            Number(item.quantity) === 0
          )
      )
    },

    currentExtraItem() {
      return this.extraItems[this.currentExtraIndex] || null
    },

    unknownItems() {
      if (!this.scanResult) return []
      return (this.scanResult.lineItems || [])
        .filter(
          (item) =>
            !item.matchedProductId &&
            !(
              item.quantity !== null &&
              item.quantity !== undefined &&
              Number(item.quantity) === 0
            ) &&
            (item.rawText ||
              (item.suggestedNewProduct && item.suggestedNewProduct.nameGuess))
        )
        .map((item, idx) => ({
          id:
            item.lineIndex != null ? String(item.lineIndex) : `unknown-${idx}`,
          description:
            (item.suggestedNewProduct && item.suggestedNewProduct.nameGuess) ||
            item.rawText ||
            '',
          quantity: item.quantity != null ? item.quantity : null
        }))
    },

    currentUnknownItem() {
      return this.unknownItems[this.currentUnknownIndex] || null
    },

    filteredUnknownProducts() {
      if (!this.unknownProductSearch || !this.products) return []
      const q = this.unknownProductSearch.toLowerCase()
      return Object.entries(this.products)
        .filter(([, p]) => p && p.name && p.name.toLowerCase().includes(q))
        .map(([id, p]) => ({ id, name: p.name }))
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 8)
    },

    stepTitle() {
      return this.step === 'complete' ? '' : 'Receiving'
    },

    analyzeMessage() {
      return this.analyzeMessages[this.analyzeMessageIndex]
    },

    autoReconciliation() {
      if (!this.workingInvoiceId) return null
      const inv = this.invoiceById(this.workingInvoiceId)
      return (inv && inv.autoReconciliation) || null
    }
  },
  watch: {
    matchedInvoice(inv) {
      if (!inv || this.scanResult) return
      if (
        inv.autoReconciliation &&
        inv.autoReconciliation.status === 'complete'
      ) {
        if (inv.scanImages && inv.scanImages.length > 0) {
          this.scanImages = inv.scanImages
        }
        this.scanResult = {
          lineItems: inv.autoReconciliation.lines || [],
          header: inv.autoReconciliation.header || null
        }
      }
    },
    step(val) {
      const locked = val !== 'vendor-select' && val !== 'complete'
      this.$store.dispatch('config/setFabVisible', !locked)
      this.$store.dispatch('config/setDragLock', locked)
      this.saveProgress()
    },
    currentItemIndex() {
      this.saveProgress()
    },
    currentExtraIndex() {
      this.saveProgress()
    },
    currentUnknownIndex() {
      this.saveProgress()
    },
    mispickItems: {
      deep: true,
      handler() {
        this.saveMispickItems()
      }
    },
    receivingItems() {
      if (!this.pendingProductId) return
      const idx = this.allItemsList.findIndex(
        (item) =>
          item.type === 'product' && item.productId === this.pendingProductId
      )
      if (idx !== -1) {
        this.currentItemIndex = idx
        this.pendingProductId = null
      }
    },
    scanProcessing(val) {
      if (val) {
        this.analyzeMessageIndex = 0
        this.analyzeTimer = setInterval(() => {
          this.analyzeMessageIndex =
            (this.analyzeMessageIndex + 1) % this.analyzeMessages.length
        }, 2200)
        // Lock FAB and sidebar while analysis is in flight
        this.$store.dispatch('config/setFabVisible', false)
        this.$store.dispatch('config/setDragLock', true)
      } else {
        clearInterval(this.analyzeTimer)
        this.analyzeTimer = null
        // Restore based on current step
        const locked = this.step !== 'vendor-select' && this.step !== 'complete'
        this.$store.dispatch('config/setFabVisible', !locked)
        this.$store.dispatch('config/setDragLock', locked)
      }
    },
    autoReconciliation(val) {
      if (!val) return
      if (val.status === 'complete') {
        this.scanProcessing = false
        this.scanResult = {
          lineItems: val.lines || [],
          header: val.header || null
        }
      } else if (val.status === 'scan_failed') {
        this.scanProcessing = false
        const instructions =
          val.scanValidation && val.scanValidation.retake_instructions
        this.scanRetakeReason =
          instructions || 'Image quality was insufficient. Please retake.'
      } else if (val.status === 'error') {
        this.scanProcessing = false
        this.scanError = val.errorMessage || 'Scan processing failed.'
      }
    }
  },
  mounted() {},
  beforeDestroy() {
    clearInterval(this.analyzeTimer)
    this.$store.dispatch('config/setFabVisible', true)
    this.$store.dispatch('config/setDragLock', false)
  },
  methods: {
    ...mapActions('storeModule/invoices', [
      'createInvoiceByVendorId',
      'updateItem',
      'closeInvoice',
      'addProductToInvoice'
    ]),

    resetFlow() {
      clearInterval(this.analyzeTimer)
      this.step = 'vendor-select'
      this.selectedVendorId = null
      this.workingInvoiceId = null
      this.scanImages = []
      this.scanResult = null
      this.scanRetakeReason = null
      this.matchedInvoice = null
      this.pendingInvoicesForVendor = []
      this.expandedInvoice = null
      this.alreadyReceivedInvoice = null
      this.creatingInvoice = false
      this.currentItemIndex = 0
      this.rejectionTarget = null
      this.mispickItems = []
      this.showOtherVendors = true
      this.scanSelectOpen = false

      this.isNewInvoice = false
      this.itemSearch = ''
      this.pendingProductId = null
      this.scanProcessing = false
      this.scanError = null
      this.analyzeMessageIndex = 0
      this.analyzeTimer = null
      this.currentExtraIndex = 0
      this.returnConfirm = null
      this.editingItem = null
      this.editingMispickIndex = null
      this.currentUnknownIndex = 0
      this.unknownAcceptModal = null
      this.unknownProductSearch = ''
      this.scanPreviewImage = null
      this.scanResultFromNewFlow = false
      this.$store.dispatch('config/setFabVisible', true)
      this.$store.dispatch('config/setDragLock', false)
    },

    saveProgress() {
      if (
        !this.workingInvoiceId ||
        this.step === 'complete' ||
        this.step === 'vendor-select'
      )
        return
      this.updateItem({
        id: this.workingInvoiceId,
        data: {
          receivingInProgress: true,
          receivingProgress: {
            step: this.step,
            currentItemIndex: this.currentItemIndex,
            currentExtraIndex: this.currentExtraIndex,
            currentUnknownIndex: this.currentUnknownIndex
          }
        }
      })
    },

    saveMispickItems() {
      if (!this.workingInvoiceId) return
      this.updateItem({
        id: this.workingInvoiceId,
        data: { receivingMispickItems: this.mispickItems }
      })
    },

    resumeInProgress(invoiceId) {
      const inv = this.invoiceById(invoiceId)
      if (!inv) return
      this.workingInvoiceId = invoiceId
      this.selectedVendorId = inv.vendorId
      this.mispickItems = inv.receivingMispickItems || []
      const progress = inv.receivingProgress || {}
      this.currentItemIndex = progress.currentItemIndex || 0
      this.currentExtraIndex = progress.currentExtraIndex || 0
      this.currentUnknownIndex = progress.currentUnknownIndex || 0
      this.step = progress.step || 'confirm'
    },

    stepLabel(step) {
      const map = {
        'po-match': 'Setting up',
        items: 'Reviewing items',
        extras: 'Reviewing extra items',
        unknowns: 'Reviewing unknown items',
        mispick: 'Recording mispick items',
        confirm: 'Ready to confirm'
      }
      return map[step] || 'In progress'
    },

    async processScanSelect() {
      this.scanProcessing = true
      this.scanError = null
      this.scanResult = null
      this.scanRetakeReason = null
      this.scanResultFromNewFlow = false

      try {
        const fn = firebase
          .app()
          .functions('us-central1')
          .httpsCallable('identifyAndScanInvoice')
        const storeId = this.$store.getters['storeModule/id']

        // Build pending invoices summary from Vuex state
        const toISOStr = (ts) => {
          if (!ts) return null
          try {
            return ts.toDate ? ts.toDate().toISOString() : null
          } catch {
            return null
          }
        }
        const pendingInvoicesSummary = this.pendingInvoiceIds
          .map((id) => {
            const inv = this.invoiceById(id)
            if (!inv || inv.completed) return null
            const items = Object.keys(inv)
              .filter(
                (k) =>
                  this.isProduct(k) &&
                  inv[k] &&
                  (inv[k].amountOrdered > 0 || inv[k].amountOrdered === false)
              )
              .map((k) => {
                const product = this.productById(k) || {}
                return {
                  productId: k,
                  name: product.name || (inv[k] && inv[k].name) || k,
                  amountOrdered: (inv[k] && inv[k].amountOrdered) || 0
                }
              })
            return {
              invoiceId: id,
              vendorId: inv.vendorId,
              vendorName: this.vendorName(inv.vendorId) || '',
              deliveryDate: toISOStr(inv.deliveryDate),
              orderDate: toISOStr(inv.orderDate),
              invoiceNumber: inv.invoiceNumber || null,
              items
            }
          })
          .filter(Boolean)

        const allVendors = (this.vendorsActive || []).map((id) => ({
          id,
          name: this.vendorName(id) || ''
        }))
        const imageURLs = this.scanImages.map((i) => i.downloadURL)
        const result = await fn({
          storeId,
          imageURLs,
          pendingInvoicesSummary,
          allVendors
        })
        const d = result.data

        this.scanProcessing = false

        if (!d.success) {
          if (d.scanFailed) {
            this.scanRetakeReason =
              (d.scanValidation && d.scanValidation.retake_instructions) ||
              'Image quality was insufficient. Please retake.'
          } else {
            this.scanError = 'Invoice scan failed. Please try again.'
          }
          return
        }

        this.scanResult = {
          lineItems: d.lineItems || [],
          header: d.header || null
        }
        this.scanResultFromNewFlow = true

        if (d.matchedVendorId) {
          this.selectedVendorId = d.matchedVendorId
          this.findMatchingPO()

          if (!this.alreadyReceivedInvoice && d.matchedInvoiceId) {
            // AI confidently matched a specific PO — use it, unless it's already completed
            const matchedPO = this.invoiceById(d.matchedInvoiceId)
            if (matchedPO && matchedPO.completed) {
              this.alreadyReceivedInvoice = matchedPO
            } else if (matchedPO) {
              this.matchedInvoice = matchedPO
              this.pendingInvoicesForVendor = []
            }
          } else if (
            !this.alreadyReceivedInvoice &&
            this.matchedInvoice &&
            this.pendingInvoicesForVendor.length === 0
          ) {
            // AI found the vendor but not a specific PO — there's exactly one pending PO.
            // Show it as a selectable option rather than auto-confirming, so the user
            // can also choose to create a new order instead.
            this.pendingInvoicesForVendor = [this.matchedInvoice]
            this.matchedInvoice = null
          }

          this.scanSelectOpen = false
          this.step = 'po-match'
        } else {
          // Vendor not recognized — keep strip visible so images stay accessible
          // and show a message directing the user to pick a vendor below.
          this.scanError =
            "Couldn't identify the vendor — select from the list below."
        }
      } catch (e) {
        console.error('identifyAndScanInvoice error:', e)
        this.scanProcessing = false
        this.scanError =
          'Could not process the invoice scan. Try again or select a vendor manually.'
      }
    },

    cancelScanSelect() {
      this.scanSelectOpen = false
      this.scanImages = []
      this.scanProcessing = false
      this.scanRetakeReason = null
      this.scanError = null
    },

    selectVendor(vendor) {
      if (vendor.inProgressInvoiceId) {
        this.resumeInProgress(vendor.inProgressInvoiceId)
        return
      }
      this.selectedVendorId = vendor.id
      this.findMatchingPO()
      this.step = 'po-match'
      if (
        this.scanImages.length > 0 &&
        this.pendingInvoicesForVendor.length === 0 &&
        this.matchedInvoice !== null
      ) {
        this.initializeAndReconcile()
      }
    },

    selectPendingInvoice(inv) {
      this.matchedInvoice = inv
      this.pendingInvoicesForVendor = []
      // When user explicitly chooses "Create new order" and a scan is already done,
      // proceed immediately without requiring another button press.
      if (!inv && this.scanResult && !this.scanRetakeReason) {
        this.confirmPOMatch()
      }
    },

    headerBack() {
      if (this.expandedInvoice) {
        this.expandedInvoice = null
      } else if (this.pendingInvoicesForVendor.length > 0) {
        this.step = 'vendor-select'
      } else {
        this.goBackInPoMatch()
      }
    },

    goBackInPoMatch() {
      this.matchedInvoice = null
      this.expandedInvoice = null
      this.scanSelectOpen = false
      this.scanResult = null
      this.scanRetakeReason = null
      this.scanError = null
      const vendorPending = this.pendingInvoiceIds
        .map((id) => this.invoiceById(id))
        .filter(
          (inv) =>
            inv && !inv.completed && inv.vendorId === this.selectedVendorId
        )
        .sort((a, b) => {
          const da = (a.deliveryDate || a.orderDate || {}).seconds || 0
          const db = (b.deliveryDate || b.orderDate || {}).seconds || 0
          return db - da
        })
      if (vendorPending.length > 1) {
        this.pendingInvoicesForVendor = vendorPending
      } else {
        this.step = 'vendor-select'
        this.selectedVendorId = null
      }
    },

    openScanCamera() {
      this.$refs.fallbackFileInput.click()
    },

    removeScanImage(img) {
      this.scanImages = this.scanImages.filter((i) => i.id !== img.id)
    },

    async handleScanFiles(event) {
      const files = Array.from(event.target.files)
      const uploads = files.map(async (file) => {
        const id = newId()
        const mediaType = file.type || 'image/jpeg'
        const localUrl = URL.createObjectURL(file)
        this.scanImages.push({
          id,
          downloadURL: localUrl,
          mediaType,
          uploading: true
        })
        try {
          const ref = storage.ref(`images/${id}`)
          await ref.put(file, { contentType: mediaType })
          const downloadURL = await ref.getDownloadURL()
          const idx = this.scanImages.findIndex((img) => img.id === id)
          if (idx !== -1) {
            this.$set(this.scanImages, idx, {
              ...this.scanImages[idx],
              downloadURL,
              uploading: false
            })
          }
        } catch (e) {
          console.error('Upload failed:', e)
        }
      })
      event.target.value = ''
      await Promise.all(uploads)
    },

    proceedFromScan() {
      this.scanRetakeReason = null
      this.tryAutoSelectVendor()
    },

    skipScan() {
      this.scanResult = null
      this.tryAutoSelectVendor()
    },

    tryAutoSelectVendor() {
      if (this.pendingVendors.length === 1) {
        const vendor = this.pendingVendors[0]
        if (vendor.inProgressInvoiceId) {
          this.resumeInProgress(vendor.inProgressInvoiceId)
          return
        }
        this.selectedVendorId = vendor.id
        this.findMatchingPO()
        this.step = 'po-match'
        if (this.matchedInvoice && this.matchedInvoice.receivingInProgress) {
          this.resumeInProgress(this.matchedInvoice.id)
          return
        }
        if (this.scanImages.length > 0 && this.matchedInvoice !== null)
          this.initializeAndReconcile()
      } else {
        this.step = 'vendor-select'
      }
    },

    findMatchingPO() {
      this.alreadyReceivedInvoice = null
      this.matchedInvoice = null
      this.pendingInvoicesForVendor = []

      const invoiceNumber =
        this.scanResult &&
        this.scanResult.header &&
        this.scanResult.header.vendorInvoiceNumber
      if (invoiceNumber) {
        const existing = Object.values(this.invoices || {}).find(
          (inv) => inv && inv.invoiceNumber === invoiceNumber && inv.completed
        )
        if (existing) {
          this.alreadyReceivedInvoice = existing
          return
        }
      }

      const vendorPending = this.pendingInvoiceIds
        .map((id) => this.invoiceById(id))
        .filter(
          (inv) =>
            inv && !inv.completed && inv.vendorId === this.selectedVendorId
        )
        .sort((a, b) => {
          const da = (a.deliveryDate || a.orderDate || {}).seconds || 0
          const db = (b.deliveryDate || b.orderDate || {}).seconds || 0
          return db - da
        })

      if (vendorPending.length === 1) {
        this.matchedInvoice = vendorPending[0]
      } else if (vendorPending.length > 1) {
        this.pendingInvoicesForVendor = vendorPending
      }
    },

    async confirmPOMatch() {
      if (!this.workingInvoiceId) {
        this.creatingInvoice = true
        if (this.matchedInvoice) {
          this.workingInvoiceId = this.matchedInvoice.id
          this.isNewInvoice = false
        } else {
          this.workingInvoiceId = await this.createInvoiceByVendorId(
            this.selectedVendorId
          )
          this.isNewInvoice = true
        }
        this.creatingInvoice = false
      }

      // When scan result came from identifyAndScanInvoice (not autoReconcileInvoice),
      // persist the images and reconciliation data to the invoice now.
      if (
        this.scanResultFromNewFlow &&
        this.scanImages.length > 0 &&
        this.scanResult
      ) {
        this.scanResultFromNewFlow = false
        await this.updateItem({
          id: this.workingInvoiceId,
          data: {
            scanImages: this.scanImages,
            downloadURL: this.scanImages[0].downloadURL,
            autoReconciliation: {
              status: 'complete',
              header: this.scanResult.header,
              lines: this.scanResult.lineItems
            }
          }
        })
      }

      // If returning to an invoice that was already scanned and confirmed, restore
      // the scan images and AI result from Firestore so the items step shows the
      // correct quantities and "Invoice scanned ✓" banner without re-scanning.
      if (!this.scanResult) {
        const existingInv = this.invoiceById(this.workingInvoiceId)
        if (
          existingInv &&
          existingInv.autoReconciliation &&
          existingInv.autoReconciliation.status === 'complete'
        ) {
          if (existingInv.scanImages && existingInv.scanImages.length > 0) {
            this.scanImages = existingInv.scanImages
          }
          this.scanResult = {
            lineItems: existingInv.autoReconciliation.lines || [],
            header: existingInv.autoReconciliation.header || null
          }
        }
      }

      // Pre-add scan-matched products not already on the PO so they appear
      // inline in invoice order rather than surfacing in a separate extras step.
      if (this.scanResult && this.scanResult.lineItems) {
        const inv = this.invoiceById(this.workingInvoiceId) || {}
        const seen = new Set()
        const batchData = {}
        this.scanResult.lineItems.forEach((item) => {
          if (!item.matchedProductId || seen.has(item.matchedProductId)) return
          seen.add(item.matchedProductId)
          if (inv[item.matchedProductId]) return
          // When receiving without a PO, use the invoice quantity as amountOrdered so
          // the item displays its expected qty and "Received" sets the correct value.
          // When a PO exists, these are genuine extras so amountOrdered stays false.
          const orderedQty = this.isNewInvoice ? item.quantity || false : false
          batchData[item.matchedProductId] = {
            amountOrdered: orderedQty,
            name:
              (this.productById(item.matchedProductId) || {}).name ||
              item.matchedProductId,
            productId: item.matchedProductId,
            amountEntered: 0,
            invoiceAction: null,
            returnQty: 0,
            returnReason: null,
            cost: item.unitPriceCents
              ? Math.round(item.unitPriceCents) / 100
              : 0,
            totalCost: 0
          }
        })
        if (Object.keys(batchData).length > 0) {
          await this.updateItem({ id: this.workingInvoiceId, data: batchData })
        }
      }

      this.currentItemIndex = 0
      this.step = 'items'
    },

    async initializeAndReconcile() {
      if (this.scanProcessing || !this.scanImages.length) return

      if (!this.workingInvoiceId) {
        this.creatingInvoice = true
        if (this.matchedInvoice) {
          this.workingInvoiceId = this.matchedInvoice.id
          this.isNewInvoice = false
        } else {
          this.workingInvoiceId = await this.createInvoiceByVendorId(
            this.selectedVendorId
          )
          this.isNewInvoice = true
        }
        this.creatingInvoice = false
      }

      // identifyAndScanInvoice already returned complete data — write it directly
      // and skip the second API call that would re-send the images.
      if (this.scanResultFromNewFlow && this.scanResult) {
        this.scanResultFromNewFlow = false
        await this.updateItem({
          id: this.workingInvoiceId,
          data: {
            scanImages: this.scanImages,
            downloadURL: this.scanImages[0].downloadURL,
            autoReconciliation: {
              status: 'complete',
              header: this.scanResult.header,
              lines: this.scanResult.lineItems
            }
          }
        })
        return
      }

      this.scanResult = null
      this.scanRetakeReason = null
      this.scanError = null
      await this.updateItem({
        id: this.workingInvoiceId,
        data: {
          scanImages: this.scanImages,
          downloadURL: this.scanImages[0].downloadURL
        }
      })
      this.runReconciliation(this.workingInvoiceId)
    },

    proceedWithoutScanning() {
      this.scanProcessing = false
      this.confirmPOMatch()
    },

    async runReconciliation(invoiceId) {
      this.scanProcessing = true
      this.scanError = null
      try {
        const fn = firebase
          .app()
          .functions('us-central1')
          .httpsCallable('autoReconcileInvoice')
        const storeId = this.$store.getters['storeModule/id']
        await fn({ invoiceId, storeId })
        // Result handled reactively by autoReconciliation watcher (VuexFire keeps invoice in sync)
      } catch (e) {
        console.error('Reconciliation error:', e)
        this.scanProcessing = false
        this.scanError =
          'Invoice scan could not be processed. Continue adding items manually.'
      }
    },

    goBackToRescan() {
      this.step = 'scan'
      this.scanResult = null
      this.scanError = null
      this.scanRetakeReason = null
      this.scanProcessing = false
    },

    acceptItem(productId) {
      const inv = this.invoiceById(this.workingInvoiceId)
      const existing = (inv && inv[productId]) || {}
      const receivedQty =
        existing.amountOrdered !== false
          ? existing.amountOrdered || 0
          : this.scanQuantityForItem(productId) || 0
      this.updateItem({
        id: this.workingInvoiceId,
        data: {
          [productId]: {
            ...existing,
            action: 'Received',
            amountReceived: receivedQty,
            amountBilled: receivedQty
          }
        }
      })
      this.advanceItem()
    },

    openRejectionFor(productId) {
      this.rejectionTarget = productId
    },

    saveRejection(payload) {
      this.updateItem({
        id: this.workingInvoiceId,
        data: { [payload.productId]: payload.itemData }
      })
      this.rejectionTarget = null
      if (this.editingItem) {
        this.editingItem = null
      } else {
        this.advanceItem()
      }
    },

    advanceItem() {
      if (this.currentItemIndex < this.allItemsList.length - 1) {
        this.currentItemIndex++
      } else {
        this.step = 'mispick'
      }
    },

    goBackItem() {
      if (this.currentItemIndex > 0) {
        this.currentItemIndex--
      } else {
        this.step = 'po-match'
      }
    },

    acceptEditItem(productId) {
      const inv = this.invoiceById(this.workingInvoiceId)
      const existing = (inv && inv[productId]) || {}
      this.updateItem({
        id: this.workingInvoiceId,
        data: {
          [productId]: {
            ...existing,
            action: 'Received',
            amountReceived:
              existing.amountOrdered || existing.amountReceived || 0,
            amountBilled: existing.amountOrdered || existing.amountReceived || 0
          }
        }
      })
      this.editingItem = null
    },

    rejectEditItem(productId) {
      this.rejectionTarget = productId
    },

    acceptExtraItem(productId) {
      const extra = this.extraItems.find(
        (e) => e.matchedProductId === productId
      )
      const qty = (extra && extra.quantity) || 0
      this.updateItem({
        id: this.workingInvoiceId,
        data: {
          [productId]: {
            action: 'Received',
            amountOrdered: 0,
            amountReceived: qty,
            amountBilled: qty
          }
        }
      })
      this.advanceExtra()
    },

    rejectExtraItem(productId) {
      const extra = this.extraItems.find(
        (e) => e.matchedProductId === productId
      )
      const qty = (extra && extra.quantity) || 0
      const product = this.productById(productId) || {}
      this.returnConfirm = {
        productId,
        productName: product.name || productId,
        qty
      }
    },

    async confirmReturn() {
      const { productId, productName, qty, isUnknown } = this.returnConfirm
      this.returnConfirm = null
      if (isUnknown) {
        this.mispickItems.push({
          id: `unknown-return-${Date.now()}`,
          description: productName,
          quantity: qty || 1,
          disposition: 'returned',
          returnReason: 'Unknown item — not in system'
        })
        if (this.step === 'items') {
          this.advanceItem()
        } else {
          this.advanceUnknown()
        }
      } else {
        await this.updateItem({
          id: this.workingInvoiceId,
          data: {
            [productId]: {
              action: 'Returned: Mispick',
              amountOrdered: 0,
              amountReceived: 0,
              amountReturned: qty,
              amountBilled: 0
            }
          }
        })
        this.advanceExtra()
      }
    },

    advanceExtra() {
      if (this.currentExtraIndex < this.extraItems.length - 1) {
        this.currentExtraIndex++
      } else if (this.unknownItems.length > 0) {
        this.currentUnknownIndex = 0
        this.step = 'unknowns'
      } else {
        this.step = 'mispick'
      }
    },

    goBackExtra() {
      if (this.currentExtraIndex > 0) {
        this.currentExtraIndex--
      } else if (this.receivingItems.length > 0) {
        this.currentItemIndex = this.receivingItems.length - 1
        this.step = 'items'
      } else {
        this.step = 'po-match'
      }
    },

    startAcceptUnknown(item) {
      this.unknownAcceptModal = {
        item: item || this.currentUnknownItem,
        matchedProductId: null,
        labelPhotoUrl: null,
        labelPhotoId: null,
        labelPhotoUploading: false,
        packagePhotoUrl: null,
        packagePhotoId: null,
        packagePhotoUploading: false
      }
      this.unknownProductSearch = ''
    },

    selectUnknownProductMatch(prod) {
      this.unknownAcceptModal = {
        ...this.unknownAcceptModal,
        matchedProductId: prod.id
      }
      this.unknownProductSearch = ''
    },

    handleUnknownLabelPhoto(event) {
      this.handleUnknownPhotoUpload(event, 'label')
    },
    handleUnknownPackagePhoto(event) {
      this.handleUnknownPhotoUpload(event, 'package')
    },
    async handleUnknownPhotoUpload(event, prefix) {
      const file = event.target.files[0]
      if (!file) return
      const id = newId()
      const urlKey = `${prefix}PhotoUrl`
      const idKey = `${prefix}PhotoId`
      const uploadingKey = `${prefix}PhotoUploading`
      this.unknownAcceptModal = {
        ...this.unknownAcceptModal,
        [urlKey]: URL.createObjectURL(file),
        [idKey]: id,
        [uploadingKey]: true
      }
      event.target.value = ''
      try {
        const ref = storage.ref(`images/${id}`)
        await ref.put(file, { contentType: file.type || 'image/jpeg' })
        const url = await ref.getDownloadURL()
        if (this.unknownAcceptModal && this.unknownAcceptModal[idKey] === id) {
          this.unknownAcceptModal = {
            ...this.unknownAcceptModal,
            [urlKey]: url,
            [uploadingKey]: false
          }
        }
      } catch (e) {
        console.error('Unknown item photo upload failed:', e)
        if (this.unknownAcceptModal) {
          this.unknownAcceptModal = {
            ...this.unknownAcceptModal,
            [uploadingKey]: false
          }
        }
      }
    },

    confirmAcceptUnknown() {
      const { item, matchedProductId, labelPhotoUrl, packagePhotoUrl } =
        this.unknownAcceptModal
      const qty = item.quantity || 1
      const product = matchedProductId
        ? this.productById(matchedProductId) || {}
        : null
      this.mispickItems.push({
        id: matchedProductId || `unknown-accept-${Date.now()}`,
        productId: matchedProductId || null,
        description: product ? product.name : item.description,
        quantity: qty,
        disposition: 'accepted',
        photoUrl: labelPhotoUrl,
        labelPhotoUrl,
        packagePhotoUrl
      })
      this.unknownAcceptModal = null
      this.unknownProductSearch = ''
      if (this.step === 'items') {
        this.advanceItem()
      } else {
        this.advanceUnknown()
      }
    },

    rejectUnknownItem(item) {
      const unknownItem = item || this.currentUnknownItem
      this.returnConfirm = {
        productName: unknownItem.description,
        qty: unknownItem.quantity,
        isUnknown: true
      }
    },

    advanceUnknown() {
      if (this.currentUnknownIndex < this.unknownItems.length - 1) {
        this.currentUnknownIndex++
      } else {
        this.step = 'mispick'
      }
    },

    goBackActiveUnknown() {
      if (this.step === 'items') {
        this.goBackItem()
      } else {
        this.goBackUnknown()
      }
    },

    goBackUnknown() {
      if (this.currentUnknownIndex > 0) {
        this.currentUnknownIndex--
      } else if (this.extraItems.length > 0) {
        this.currentExtraIndex = this.extraItems.length - 1
        this.step = 'extras'
      } else if (this.receivingItems.length > 0) {
        this.currentItemIndex = this.receivingItems.length - 1
        this.step = 'items'
      } else {
        this.step = 'po-match'
      }
    },

    addProductToReceiving(productId) {
      this.addProductToInvoice({ invoiceId: this.workingInvoiceId, productId })
      this.itemSearch = ''
    },

    backFromMispick() {
      if (this.unknownItems.length > 0) {
        this.currentUnknownIndex = this.unknownItems.length - 1
        this.step = 'unknowns'
      } else if (this.extraItems.length > 0) {
        this.currentExtraIndex = this.extraItems.length - 1
        this.step = 'extras'
      } else if (this.receivingItems.length > 0) {
        this.currentItemIndex = this.receivingItems.length - 1
        this.step = 'items'
      } else {
        this.step = 'po-match'
      }
    },

    handleConfirmationProductAdd(productId) {
      this.pendingProductId = productId
      this.step = 'items'
    },

    addMispickItem(item) {
      this.mispickItems.push(item)
      if (item.productId) {
        const location = (this.productById(item.productId) || {}).location
        if (location) this.mispickPutAwayLocation = location
      }
    },

    removeMispick(idx) {
      this.mispickItems.splice(idx, 1)
    },

    removeMispickFromEdit() {
      this.mispickItems.splice(this.editingMispickIndex, 1)
      this.editingMispickIndex = null
    },

    updateMispickItem(item) {
      this.$set(this.mispickItems, this.editingMispickIndex, item)
      this.editingMispickIndex = null
    },

    toggleMispickDisposition() {
      const item = this.mispickItems[this.editingMispickIndex]
      this.$set(this.mispickItems, this.editingMispickIndex, {
        ...item,
        disposition: item.disposition === 'returned' ? 'accepted' : 'returned'
      })
      this.editingMispickIndex = null
    },

    async submitReceiving() {
      if (this.mispickItems.length > 0) {
        const mispickData = {}
        this.mispickItems.forEach((item) => {
          const isReturned = item.disposition === 'returned'
          mispickData[item.id] = {
            productId: item.productId || item.id,
            name: item.description,
            mispick: true,
            amountReceived: item.quantity,
            amountOrdered: 0,
            amountBilled: isReturned ? 0 : item.quantity,
            amountReturned: isReturned ? item.quantity : 0,
            action: isReturned ? 'Returned: Mispick' : 'Received',
            returnReason: item.returnReason || null,
            photos: [
              ...(item.photoUrl
                ? [{ url: item.photoUrl, type: 'product' }]
                : []),
              ...(item.returnPhotoUrl
                ? [{ url: item.returnPhotoUrl, type: 'return' }]
                : [])
            ],
            cost: 0,
            totalCost: 0
          }
        })
        await this.updateItem({ id: this.workingInvoiceId, data: mispickData })
      }
      // Build the closing update in one batch
      const inv = this.invoiceById(this.workingInvoiceId) || {}
      const closingData = {
        receivingMethod: this.scanResult ? 'scan' : 'manual',
        receivingInProgress: false,
        receivingProgress: null,
        receivingMispickItems: null
      }

      // Promote scan header fields to top-level invoice fields that Reports reads
      const scanHeader =
        (inv.autoReconciliation && inv.autoReconciliation.header) ||
        (this.scanResult && this.scanResult.header) ||
        null

      if (scanHeader) {
        if (scanHeader.vendorInvoiceNumber && !inv.invoiceNumber) {
          closingData.invoiceNumber = scanHeader.vendorInvoiceNumber
        }
        if (scanHeader.invoiceDate && !inv.invoiceDate) {
          try {
            const dateObj = DateTime.fromISO(scanHeader.invoiceDate, {
              zone: 'America/New_York'
            }).toJSDate()
            closingData.invoiceDate =
              firebase.firestore.Timestamp.fromDate(dateObj)
          } catch (e) {
            /* invalid date — skip */
          }
        }
      }

      // Compute per-item totalCost (amountReceived * cost) for the line items
      Object.keys(inv).forEach((key) => {
        if (
          this.isProduct(key) &&
          inv[key] &&
          typeof inv[key].cost === 'number'
        ) {
          closingData[`${key}.totalCost`] =
            (inv[key].amountReceived || 0) * inv[key].cost
        }
      })

      // Invoice-level totalCost should reflect exactly what's on the physical invoice.
      // Returns logged by staff are internal records only — they don't change the invoice amount.
      // Prefer the scan header's total (already includes vendor credits/returns);
      // fall back to summing per-item received costs + fees/tax for manual-entry invoices.
      const scanHeaderTotalCents =
        inv.autoReconciliation &&
        inv.autoReconciliation.header &&
        typeof inv.autoReconciliation.header.totalAmountCents === 'number'
          ? inv.autoReconciliation.header.totalAmountCents
          : null

      if (scanHeaderTotalCents !== null) {
        closingData.totalCost = scanHeaderTotalCents / 100
      } else {
        let invoiceTotalCost = 0
        Object.keys(inv).forEach((key) => {
          if (
            this.isProduct(key) &&
            inv[key] &&
            typeof inv[key].cost === 'number'
          ) {
            invoiceTotalCost += (inv[key].amountReceived || 0) * inv[key].cost
          }
        })
        invoiceTotalCost += (inv.fees || 0) + (inv.tax || 0)
        closingData.totalCost = invoiceTotalCost
      }

      await this.updateItem({ id: this.workingInvoiceId, data: closingData })
      await this.closeInvoice(this.workingInvoiceId)
      this.step = 'complete'
    },

    resetState() {
      const initial = this.$options.data.call(this)
      Object.keys(initial).forEach((key) => {
        this[key] = initial[key]
      })
      this.$store.dispatch('config/setFabVisible', true)
      this.$store.dispatch('config/setDragLock', false)
    },

    confirmExit() {
      if (
        this.step === 'vendor-select' ||
        this.step === 'scan' ||
        this.step === 'complete' ||
        !this.workingInvoiceId
      ) {
        this.resetState()
        return
      }
      this.$modal.show('modal--confirm', {
        title: 'Exit Receiving?',
        text: 'Your progress is saved. You can resume where you left off.',
        buttons: [
          {
            title: 'Cancel',
            handler: () => this.$modal.hide('modal--confirm')
          },
          {
            title: 'Exit',
            handler: () => {
              this.$modal.hide('modal--confirm')
              this.resetState()
            }
          }
        ]
      })
    },

    poItemCount(invoice) {
      if (!invoice) return 0
      return Object.keys(invoice).filter((k) => this.isProduct(k)).length
    },

    itemsForInvoice(invoice) {
      if (!invoice) return []
      return Object.keys(invoice)
        .filter(
          (k) =>
            this.isProduct(k) &&
            invoice[k] &&
            (invoice[k].amountOrdered > 0 || invoice[k].amountOrdered === false)
        )
        .map((k) => {
          const product = this.productById(k) || {}
          const qty = invoice[k].amountOrdered
          return {
            id: k,
            name: product.name || invoice[k].name || k,
            code: product.code || null,
            amountOrdered: qty === false || qty == null ? '—' : qty,
            unit: product.unit || ''
          }
        })
        .sort((a, b) => a.name.localeCompare(b.name))
    },

    scanQuantityForItem(productId) {
      if (!this.scanResult || !this.scanResult.lineItems) return null
      const match = this.scanResult.lineItems.find(
        (l) => l.matchedProductId === productId
      )
      return match ? match.quantity : null
    },

    formatDate(ts) {
      if (!ts) return ''
      try {
        const d = ts.toDate ? ts.toDate() : new Date(ts)
        return DateTime.fromJSDate(d).toFormat('LLL d, yyyy')
      } catch {
        return ''
      }
    }
  }
}
</script>

<style scoped>
/* Scan line that sweeps across the image strip during analysis */
.scan-line-overlay {
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: 0.5rem;
  z-index: 1;
}

.scan-line-overlay::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(
    to right,
    transparent,
    rgba(255, 255, 255, 0.9),
    transparent
  );
  box-shadow: 0 0 10px 3px rgba(255, 255, 255, 0.5);
  animation: scanSweep 1.8s ease-in-out infinite;
}

@keyframes scanSweep {
  0% {
    top: 0%;
    opacity: 0;
  }
  8% {
    opacity: 1;
  }
  92% {
    opacity: 1;
  }
  100% {
    top: 100%;
    opacity: 0;
  }
}

/* Fade transition for cycling messages */
.analyze-msg-enter-active,
.analyze-msg-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.analyze-msg-enter {
  opacity: 0;
  transform: translateY(4px);
}

.analyze-msg-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
